import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Alert, Spinner, Badge } from 'react-bootstrap';
import { FaUpload, FaTrash, FaRocket, FaCheckCircle, FaExclamationTriangle, FaFileAlt, FaImage, FaSyncAlt, FaDownload, FaArrowRight, FaArrowLeft, FaEye, FaHistory } from 'react-icons/fa';
import Layout from './Layout';
import SdRetargetingCampaignAPI from '../services/SdRetargetingCampaignAPI';
import '../assets/css/SdRetargetingCampaigns.css';

const SdRetargetingCampaigns = () => {
    // Files
    const [ownProductsFile, setOwnProductsFile] = useState(null);
    const [retargetingFile, setRetargetingFile] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);

    // Processed Data
    const [ownProducts, setOwnProducts] = useState([]);
    const [retargetingProducts, setRetargetingProducts] = useState([]);
    const [selectedOwnProducts, setSelectedOwnProducts] = useState([]);
    const [selectedRetargetingProducts, setSelectedRetargetingProducts] = useState([]);

    // Retargeting Strategy Settings
    const [retargetingType, setRetargetingType] = useState('asinViews'); // 'asinViews' (Visited advertised ASINs) or 'similarProductViews' (Visited similar products)
    const [lookbackWindow, setLookbackWindow] = useState(30); // 7, 14, 30, 60, 90 days

    // Campaign Configuration
    const [campaignName, setCampaignName] = useState('');
    const [headline, setHeadline] = useState('');
    const [dailyBudget, setDailyBudget] = useState(15);
    const [defaultBid, setDefaultBid] = useState(1.0);
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState('');
    const [bidOptimization, setBidOptimization] = useState('conversions');
    const [campaignState, setCampaignState] = useState('paused');

    // UI States
    const [loading, setLoading] = useState(false);
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [step, setStep] = useState(1); // 1: Upload, 2: Select, 3: Configure, 4: Results

    const getProfileId = () => {
        return localStorage.getItem('selectedAccountValue') || null;
    };

    const handleOwnProductsChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) {
            setOwnProductsFile(file);
        }
    };

    const handleRetargetingChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) {
            setRetargetingFile(file);
        }
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setLogoPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleUploadFiles = async () => {
        if (!ownProductsFile) {
            setError('Please upload at least the Promoted Products CSV file.');
            return;
        }

        setUploadingFiles(true);
        setError(null);

        try {
            const response = await SdRetargetingCampaignAPI.uploadFiles(ownProductsFile, retargetingFile, logoFile);
            if (response.success) {
                const prods = response.data.ownProducts || [];
                let retargetList = response.data.retargetingProducts || [];

                // If no separate retargeting file was uploaded, default to retargeting views of own products
                if (retargetList.length === 0 && prods.length > 0) {
                    retargetList = prods.map(p => ({ asin: p.asin, bid: defaultBid, lookbackWindow: lookbackWindow }));
                }

                setOwnProducts(prods);
                setRetargetingProducts(retargetList);
                setSelectedOwnProducts(prods.length > 3 ? prods.slice(0, 3).map(p => p.asin) : prods.map(p => p.asin));
                setSelectedRetargetingProducts(retargetList.length > 3 ? retargetList.slice(0, 3).map(r => r.asin) : retargetList.map(r => r.asin));

                if (!campaignName && prods.length > 0) {
                    setCampaignName(`SD_RETARGETING_${prods[0].asin}_${lookbackWindow}D_${new Date().toISOString().slice(0, 10)}`);
                }

                setStep(2);
            } else {
                setError(response.error || 'Error processing files');
            }
        } catch (err) {
            setError(err.message || 'Error uploading files');
        } finally {
            setUploadingFiles(false);
        }
    };

    const toggleOwnProduct = (asin) => {
        setSelectedOwnProducts(prev =>
            prev.includes(asin) ? prev.filter(a => a !== asin) : [...prev, asin]
        );
    };

    const toggleRetargetingProduct = (asin) => {
        setSelectedRetargetingProducts(prev =>
            prev.includes(asin) ? prev.filter(a => a !== asin) : [...prev, asin]
        );
    };

    const handleCreateCampaign = async () => {
        const profileId = getProfileId();
        if (!profileId) {
            setError('Please select an active Amazon Ads account in the top bar before proceeding.');
            return;
        }

        if (!campaignName.trim()) {
            setError('Campaign name is required');
            return;
        }

        if (selectedOwnProducts.length === 0) {
            setError('Please select at least one promoted product');
            return;
        }

        if (selectedRetargetingProducts.length === 0) {
            setError('Please select at least one product to retarget');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const targetAsinList = retargetingProducts.filter(r => selectedRetargetingProducts.includes(r.asin));

            const response = await SdRetargetingCampaignAPI.createCampaign({
                profileId,
                campaignName,
                dailyBudget,
                startDate,
                endDate: endDate || undefined,
                headline,
                ownProducts: selectedOwnProducts,
                retargetingAsins: targetAsinList,
                lookbackWindow,
                retargetingType,
                bidOptimization,
                defaultBid,
                state: campaignState,
                logo: logoFile
            });

            setResult(response);
            setStep(4);
        } catch (err) {
            setError(err.message || 'Error creating retargeting campaign');
        } finally {
            setLoading(false);
        }
    };

    const downloadSampleCSV = (type) => {
        let content = '';
        let filename = '';
        if (type === 'products') {
            content = 'ASIN,SKU,Price,Title\nB08N5WRWNW,MY-PROD-01,29.99,Wireless Noise Cancelling Headphones\nB09V3HBK6L,MY-PROD-02,49.99,Smart Fitness Watch';
            filename = 'sample_own_products.csv';
        } else {
            content = 'ASIN,Bid,LookbackWindow\nB08N5WRWNW,1.20,30\nB09V3HBK6L,1.50,14\nB07XJ8C8F5,1.00,30';
            filename = 'sample_retargeting_asins.csv';
        }
        const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const resetForm = () => {
        setStep(1);
        setOwnProductsFile(null);
        setRetargetingFile(null);
        setLogoFile(null);
        setLogoPreview(null);
        setOwnProducts([]);
        setRetargetingProducts([]);
        setSelectedOwnProducts([]);
        setSelectedRetargetingProducts([]);
        setCampaignName('');
        setResult(null);
        setError(null);
    };

    return (
        <Layout>
            <div className="sd-retargeting-campaigns-page">
                {/* Header Banner */}
                <div className="sd-header-banner">
                    <div>
                        <div className="sd-header-title">
                            <FaSyncAlt className="text-primary" /> Sponsored Display — Retargeting Campaigns
                        </div>
                        <p className="sd-header-subtitle">
                            Re-engage shoppers who viewed your products or similar competitor items in the last 7 to 90 days.
                        </p>
                    </div>
                    <div className="d-flex gap-2">
                        <Button variant="outline-light" size="sm" onClick={() => downloadSampleCSV('products')}>
                            <FaDownload className="me-1" /> Products Template
                        </Button>
                        <Button variant="outline-light" size="sm" onClick={() => downloadSampleCSV('retargeting')}>
                            <FaDownload className="me-1" /> Retargeting Template
                        </Button>
                    </div>
                </div>

                {/* Progress Steps */}
                <div className="progress-steps">
                    <div className={`step ${step === 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`} onClick={() => step > 1 && setStep(1)}>
                        <div className="step-number">{step > 1 ? <FaCheckCircle /> : '1'}</div>
                        <span className="step-label">Upload Files</span>
                    </div>
                    <div className={`step ${step === 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`} onClick={() => step > 2 && setStep(2)}>
                        <div className="step-number">{step > 2 ? <FaCheckCircle /> : '2'}</div>
                        <span className="step-label">Select ASINs</span>
                    </div>
                    <div className={`step ${step === 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''}`} onClick={() => step > 3 && setStep(3)}>
                        <div className="step-number">{step > 3 ? <FaCheckCircle /> : '3'}</div>
                        <span className="step-label">Strategy Setup</span>
                    </div>
                    <div className={`step ${step === 4 ? 'active' : ''}`}>
                        <div className="step-number">4</div>
                        <span className="step-label">Results</span>
                    </div>
                </div>

                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)} className="d-flex align-items-center mb-4">
                        <FaExclamationTriangle className="me-2 fs-5" />
                        <div>{error}</div>
                    </Alert>
                )}

                {/* STEP 1: Upload Files */}
                {step === 1 && (
                    <div>
                        <Row className="g-4 mb-4">
                            {/* Promoted Products Upload */}
                            <Col md={4}>
                                <Card className="upload-card">
                                    <Card.Body>
                                        <div className="card-title">
                                            <FaFileAlt className="text-primary" /> Promoted Products (CSV) *
                                        </div>
                                        <label className="upload-area w-100">
                                            <input type="file" accept=".csv" onChange={handleOwnProductsChange} style={{ display: 'none' }} />
                                            <FaUpload />
                                            <div>{ownProductsFile ? ownProductsFile.name : 'Upload your advertised ASINs'}</div>
                                            <small className="text-muted mt-1">Columns: ASIN, SKU, Title, Price</small>
                                        </label>
                                        {ownProductsFile && (
                                            <div className="file-info-badge">
                                                <FaCheckCircle className="text-success" /> {ownProductsFile.name} ({(ownProductsFile.size / 1024).toFixed(1)} KB)
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Retargeted ASINs Upload */}
                            <Col md={4}>
                                <Card className="upload-card">
                                    <Card.Body>
                                        <div className="card-title">
                                            <FaHistory className="text-info" /> Retargeted ASINs (Optional CSV)
                                        </div>
                                        <label className="upload-area w-100">
                                            <input type="file" accept=".csv" onChange={handleRetargetingChange} style={{ display: 'none' }} />
                                            <FaUpload />
                                            <div>{retargetingFile ? retargetingFile.name : 'Upload specific ASINs to retarget'}</div>
                                            <small className="text-muted mt-1">If blank, will automatically retarget own products</small>
                                        </label>
                                        {retargetingFile && (
                                            <div className="file-info-badge">
                                                <FaCheckCircle className="text-success" /> {retargetingFile.name} ({(retargetingFile.size / 1024).toFixed(1)} KB)
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Brand Logo Upload */}
                            <Col md={4}>
                                <Card className="upload-card">
                                    <Card.Body>
                                        <div className="card-title">
                                            <FaImage className="text-warning" /> Brand Logo (Optional)
                                        </div>
                                        <label className="upload-area w-100">
                                            <input type="file" accept="image/*" onChange={handleLogoChange} style={{ display: 'none' }} />
                                            {logoPreview ? (
                                                <img src={logoPreview} alt="Logo preview" style={{ maxHeight: '60px', maxWidth: '100%', objectFit: 'contain' }} />
                                            ) : (
                                                <>
                                                    <FaImage />
                                                    <div>Click to upload brand logo image</div>
                                                    <small className="text-muted mt-1">PNG, JPG recommended 600x100px</small>
                                                </>
                                            )}
                                        </label>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end">
                            <Button className="btn-primary-sd" onClick={handleUploadFiles} disabled={uploadingFiles || !ownProductsFile}>
                                {uploadingFiles ? <Spinner size="sm" animation="border" className="me-2" /> : <FaArrowRight className="me-2" />}
                                Continue to Selection
                            </Button>
                        </div>
                    </div>
                )}

                {/* STEP 2: Selection & Review */}
                {step === 2 && (
                    <div>
                        <Row className="g-4 mb-4">
                            {/* Promoted Products Table */}
                            <Col md={6}>
                                <Card className="product-card">
                                    <Card.Header className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <strong>Promoted Products ({selectedOwnProducts.length}/{ownProducts.length})</strong>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <Button size="sm" variant="outline-light" onClick={() => setSelectedOwnProducts(ownProducts.map(p => p.asin))}>Select All</Button>
                                            <Button size="sm" variant="outline-light" onClick={() => setSelectedOwnProducts([])}>Clear</Button>
                                        </div>
                                    </Card.Header>
                                    <div className="product-list">
                                        <Table responsive hover size="sm">
                                            <thead>
                                                <tr>
                                                    <th width="40"></th>
                                                    <th>ASIN</th>
                                                    <th>Title</th>
                                                    <th>Price</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {ownProducts.map(prod => (
                                                    <tr key={prod.asin} className={selectedOwnProducts.includes(prod.asin) ? 'table-active' : ''} onClick={() => toggleOwnProduct(prod.asin)} style={{ cursor: 'pointer' }}>
                                                        <td>
                                                            <Form.Check type="checkbox" checked={selectedOwnProducts.includes(prod.asin)} onChange={() => {}} />
                                                        </td>
                                                        <td className="font-monospace text-info">{prod.asin}</td>
                                                        <td className="text-truncate" style={{ maxWidth: '200px' }}>{prod.title || prod.sku || '—'}</td>
                                                        <td>{prod.price ? `$${prod.price.toFixed(2)}` : '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card>
                            </Col>

                            {/* Retargeted ASINs Table */}
                            <Col md={6}>
                                <Card className="product-card">
                                    <Card.Header className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <strong>ASINs to Retarget ({selectedRetargetingProducts.length}/{retargetingProducts.length})</strong>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <Button size="sm" variant="outline-light" onClick={() => setSelectedRetargetingProducts(retargetingProducts.map(r => r.asin))}>Select All</Button>
                                            <Button size="sm" variant="outline-light" onClick={() => setSelectedRetargetingProducts([])}>Clear</Button>
                                        </div>
                                    </Card.Header>
                                    <div className="product-list">
                                        <Table responsive hover size="sm">
                                            <thead>
                                                <tr>
                                                    <th width="40"></th>
                                                    <th>ASIN</th>
                                                    <th>Lookback</th>
                                                    <th>Bid</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {retargetingProducts.map(item => (
                                                    <tr key={item.asin} className={selectedRetargetingProducts.includes(item.asin) ? 'table-active' : ''} onClick={() => toggleRetargetingProduct(item.asin)} style={{ cursor: 'pointer' }}>
                                                        <td>
                                                            <Form.Check type="checkbox" checked={selectedRetargetingProducts.includes(item.asin)} onChange={() => {}} />
                                                        </td>
                                                        <td className="font-monospace text-warning">{item.asin}</td>
                                                        <td><Badge bg="secondary">{item.lookbackWindow || lookbackWindow} Days</Badge></td>
                                                        <td>${(item.bid || defaultBid).toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-between">
                            <Button variant="secondary" onClick={() => setStep(1)}>
                                <FaArrowLeft className="me-2" /> Back
                            </Button>
                            <Button className="btn-primary-sd" onClick={() => setStep(3)} disabled={selectedOwnProducts.length === 0 || selectedRetargetingProducts.length === 0}>
                                Configure Strategy <FaArrowRight className="ms-2" />
                            </Button>
                        </div>
                    </div>
                )}

                {/* STEP 3: Campaign Setup */}
                {step === 3 && (
                    <div>
                        <Row className="g-4 mb-4">
                            <Col md={7}>
                                <Card className="config-card">
                                    <Card.Header>
                                        <FaRocket className="me-2 text-primary" /> Retargeting Strategy & Parameters
                                    </Card.Header>
                                    <Card.Body>
                                        <Form>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Campaign Name *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={campaignName}
                                                    onChange={(e) => setCampaignName(e.target.value)}
                                                    placeholder="e.g. SD_Retargeting_Views_30D"
                                                />
                                            </Form.Group>

                                            {/* Retargeting Lookback Window */}
                                            <Form.Group className="mb-3">
                                                <Form.Label className="d-flex align-items-center gap-2">
                                                    <FaHistory className="text-info" /> Lookback Window (Days since shopper viewed product)
                                                </Form.Label>
                                                <div className="lookback-pill-group">
                                                    {[7, 14, 30, 60, 90].map(days => (
                                                        <div
                                                            key={days}
                                                            className={`lookback-pill ${lookbackWindow === days ? 'active' : ''}`}
                                                            onClick={() => setLookbackWindow(days)}
                                                        >
                                                            {days} Days
                                                        </div>
                                                    ))}
                                                </div>
                                            </Form.Group>

                                            {/* Retargeting Tactic Type */}
                                            <Form.Group className="mb-3">
                                                <Form.Label>Retargeting Tactic</Form.Label>
                                                <Form.Select
                                                    value={retargetingType}
                                                    onChange={(e) => setRetargetingType(e.target.value)}
                                                >
                                                    <option value="asinViews">Views Remarketing (Shoppers who viewed advertised products)</option>
                                                    <option value="similarProductViews">Similar Product Views (Shoppers who viewed similar competitor products)</option>
                                                </Form.Select>
                                            </Form.Group>

                                            <Row className="mb-3">
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label>Daily Budget ($) *</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            step="1"
                                                            min="1"
                                                            value={dailyBudget}
                                                            onChange={(e) => setDailyBudget(e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label>Default Bid ($) *</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.05"
                                                            min="0.02"
                                                            value={defaultBid}
                                                            onChange={(e) => setDefaultBid(e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3">
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label>Start Date</Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            value={startDate}
                                                            onChange={(e) => setStartDate(e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label>End Date (Optional)</Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            value={endDate}
                                                            onChange={(e) => setEndDate(e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3">
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label>Bid Optimization</Form.Label>
                                                        <Form.Select
                                                            value={bidOptimization}
                                                            onChange={(e) => setBidOptimization(e.target.value)}
                                                        >
                                                            <option value="conversions">Optimize for Conversions (Recommended for retargeting)</option>
                                                            <option value="clicks">Optimize for Page Visits (Clicks)</option>
                                                            <option value="reach">Optimize for Viewable Impressions</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label>Initial State</Form.Label>
                                                        <Form.Select
                                                            value={campaignState}
                                                            onChange={(e) => setCampaignState(e.target.value)}
                                                        >
                                                            <option value="paused">Paused (Recommended for safety)</option>
                                                            <option value="enabled">Enabled (Active immediately)</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Form.Group className="mb-3">
                                                <Form.Label>Ad Headline (Optional)</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={headline}
                                                    onChange={(e) => setHeadline(e.target.value)}
                                                    placeholder="e.g. Come back and finish your order!"
                                                />
                                            </Form.Group>
                                        </Form>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col md={5}>
                                <Card className="config-card">
                                    <Card.Header>
                                        <strong>Summary Review</strong>
                                    </Card.Header>
                                    <Card.Body>
                                        <ul className="summary-list">
                                            <li>
                                                <span className="text-muted">Tactic:</span>
                                                <Badge bg="primary">Views Remarketing (T00020)</Badge>
                                            </li>
                                            <li>
                                                <span className="text-muted">Lookback Window:</span>
                                                <Badge bg="info">{lookbackWindow} Days</Badge>
                                            </li>
                                            <li>
                                                <span className="text-muted">Promoted Products:</span>
                                                <Badge bg="info">{selectedOwnProducts.length} ASINs</Badge>
                                            </li>
                                            <li>
                                                <span className="text-muted">Retargeted Items:</span>
                                                <Badge bg="warning" text="dark">{selectedRetargetingProducts.length} ASINs</Badge>
                                            </li>
                                            <li>
                                                <span className="text-muted">Daily Budget:</span>
                                                <strong className="text-success">${parseFloat(dailyBudget).toFixed(2)}/day</strong>
                                            </li>
                                            <li>
                                                <span className="text-muted">Optimization:</span>
                                                <span className="text-capitalize">{bidOptimization}</span>
                                            </li>
                                            <li>
                                                <span className="text-muted">Logo Included:</span>
                                                <span>{logoFile ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>}</span>
                                            </li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-between">
                            <Button variant="secondary" onClick={() => setStep(2)}>
                                <FaArrowLeft className="me-2" /> Back
                            </Button>
                            <Button className="btn-primary-sd" onClick={handleCreateCampaign} disabled={loading}>
                                {loading ? <Spinner size="sm" animation="border" className="me-2" /> : <FaRocket className="me-2" />}
                                Launch Retargeting Campaign
                            </Button>
                        </div>
                    </div>
                )}

                {/* STEP 4: Execution Results */}
                {step === 4 && result && (
                    <Card className="result-card p-4">
                        <div className="text-center mb-4">
                            <FaCheckCircle className="text-success display-3 mb-3" />
                            <h3>Sponsored Display Retargeting Campaign Created!</h3>
                            <p className="text-muted">Your remarketing campaign is configured and sent to Amazon Advertising API.</p>
                        </div>

                        <Row className="g-3 mb-4">
                            <Col md={4}>
                                <div className="result-stat-box">
                                    <small className="text-muted d-block fw-bold text-uppercase" style={{ fontSize: '0.75rem' }}>Campaign ID</small>
                                    <strong className="text-primary fs-5">{result.data?.campaign?.campaignId || 'Generated'}</strong>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="result-stat-box">
                                    <small className="text-muted d-block fw-bold text-uppercase" style={{ fontSize: '0.75rem' }}>Ad Group ID</small>
                                    <strong className="text-secondary fs-5">{result.data?.adGroup?.adGroupId || 'Generated'}</strong>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="result-stat-box">
                                    <small className="text-muted d-block fw-bold text-uppercase" style={{ fontSize: '0.75rem' }}>Lookback Window</small>
                                    <strong className="text-success fs-5">{lookbackWindow} Days</strong>
                                </div>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-center gap-3">
                            <Button variant="outline-dark" onClick={resetForm}>
                                Create Another Campaign
                            </Button>
                        </div>
                    </Card>
                )}
            </div>
        </Layout>
    );
};

export default SdRetargetingCampaigns;
